import { ProductType } from 'marketplace-ui-common/types/products';
import { useTrackMarketplaceEvent } from 'marketplace-ui-tracking';
import { getSearchResultClickedEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback } from 'react';
import { AutocompleteCard } from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc';
import { AutocompleteResultItem } from '../../__generated__/chirp/com/hubspot/query/understanding/mkpl/autocomp/rpc/MarketplaceAutocompleteRpc';
import {
  getProductTypeByAutocompleteResultItem,
  getSourceIdByAutocompleteResultItem,
} from '../utils/usageTracker';

export function useTrackAutocompleteSearchResultEvent({
  resultCount,
  searchTerm,
}: {
  resultCount: number;
  searchTerm: string;
}) {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();

  const getAutocompleteSearchResultEventProps = useCallback(
    (item: AutocompleteResultItem, resultIndex: number) => {
      return {
        companyName: item.providerName,
        componentType: 'AutocompleteItem',
        listingId:
          item.resultType === 'COLLECTION' ? item.collectionId : item.listingId,
        resultCount,
        resultIndex,
        resultType: String(item.resultType),
        search: searchTerm,
        slug: item.slug,
        sourceId: getSourceIdByAutocompleteResultItem(item),
        type: getProductTypeByAutocompleteResultItem(item),
        value: item.resultType === 'QUERY' ? item.term : undefined,
        ...(item.resultType === 'COLLECTION'
          ? {
              category: item.slug,
              collectionId: item.collectionId,
            }
          : {}),
      };
    },
    [resultCount, searchTerm]
  );

  const trackAutocompleteSearchResultClickedEvent = useCallback(
    (item: AutocompleteResultItem, resultIndex: number) => {
      trackMarketplaceEvent(
        getSearchResultClickedEvent(
          getAutocompleteSearchResultEventProps(item, resultIndex)
        )
      );
    },
    [getAutocompleteSearchResultEventProps, trackMarketplaceEvent]
  );

  return {
    getAutocompleteSearchResultEventProps,
    trackAutocompleteSearchResultClickedEvent,
  };
}

export function useTrackAutocompleteSearchResultEvent2({
  resultCount,
  searchTerm,
}: {
  resultCount: number;
  searchTerm: string;
}) {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();

  const getAutocompleteSearchResultEventProps = useCallback(
    (item: AutocompleteCard, resultIndex: number) => {
      const products = item.listing?.card.products;

      return {
        companyName: item.listing?.card.companyName,
        componentType: 'AutocompleteItem',
        listingId:
          item.resultType === 'COLLECTION'
            ? item.collection?.collectionId
            : item.listing?.card.listingId,
        resultCount,
        resultIndex,
        resultType: String(item.resultType),
        search: searchTerm,
        slug: item.listing?.card.slug,
        sourceId: products?.map(
          (product) => `${String(product.productType)}__${product.sourceId}`
        ),
        type: products?.length
          ? (products[0]?.productType as ProductType)
          : undefined,
        value: item.resultType === 'QUERY' ? item.term : undefined,
        ...(item.resultType === 'COLLECTION'
          ? {
              category: item.collection?.slug,
              collectionId: item.collection?.collectionId,
            }
          : {}),
      };
    },
    [resultCount, searchTerm]
  );

  const trackAutocompleteSearchResultClickedEvent = useCallback(
    (item: AutocompleteCard, resultIndex: number) => {
      trackMarketplaceEvent(
        getSearchResultClickedEvent(
          getAutocompleteSearchResultEventProps(item, resultIndex)
        )
      );
    },
    [getAutocompleteSearchResultEventProps, trackMarketplaceEvent]
  );

  return {
    getAutocompleteSearchResultEventProps,
    trackAutocompleteSearchResultClickedEvent,
  };
}
